import { type ReactNode, type RefObject, useEffect, useMemo, useRef, useState } from "react";
import {
    type BlurEvent,
    type FocusEvent,
    StyleSheet,
    TextInput as TextInputBase,
    type TextInputProps as TextInputBaseProps,
    type TextStyle,
    View,
    type ViewStyle,
} from "react-native";
import { FieldLabel } from "~/components/form/FieldLabel";
import { BaseText } from "~/components/layout/text";
import { Space } from "~/constants/sizing";
import { NoName, useErrorBag } from "~/providers/ErrorBagProvider";
import { useTheme } from "~/providers/ThemeProvider";
import { empty, filled, isEmail } from "~/util";
import { setWidthOnLayout } from "~/util/layout";

export type TextInputProps = {
    name?: string;
    label?: string;
    optional?: boolean;
    error?: string | null;
    disabled?: boolean;
    left?: string | (() => ReactNode) | null | undefined;
    right?: string | (() => ReactNode) | null | undefined;
    hideLabel?: boolean;
    labelStyle?: TextStyle;
    containerStyle?: ViewStyle;
    hint?: ReactNode;
    ref?: RefObject<TextInputBase | null>;
} & TextInputBaseProps;

export const TextInput = ({
    label = "",
    name = NoName,
    optional,
    error,
    hint,
    disabled,
    editable,
    left,
    right,
    hideLabel,
    labelStyle,
    containerStyle,
    style,
    ref,
    onFocus,
    onBlur,
    onChangeText,
    ...textInputProps
}: TextInputProps) => {
    const { inputs } = useTheme();

    const errorBag = useErrorBag();
    const show_label = Boolean(label?.trim()?.length);
    const is_editable = useMemo<boolean>(() => editable ?? !disabled, [editable, disabled]);
    const include_left = typeof left === "string" ? () => <BaseText colour={"secondary"}>{left}</BaseText> : left;
    const include_right = typeof right === "string" ? () => <BaseText colour={"secondary"}>{right}</BaseText> : right;
    const [focused, setFocused] = useState(false);
    const [touched, setTouched] = useState(false);
    const [last, setLast] = useState("");

    const local_ref = useRef(null);
    const field_ref = ref ?? local_ref;

    const [leftWidth, setLeftWidth] = useState(include_left ? 30 : 0);
    const [rightWidth, setRightWidth] = useState(include_right ? 30 : 0);

    const onLeftLayout = setWidthOnLayout(setLeftWidth);
    const onRightLayout = setWidthOnLayout(setRightWidth);

    const paddingLeft = useMemo(() => 16 + (leftWidth ? leftWidth + 8 : 0), [leftWidth]);
    const paddingRight = useMemo(() => 16 + (rightWidth ? rightWidth + 8 : 0), [rightWidth]);

    const field_error = error ?? errorBag.first(name);

    const handleOnFocus = (event: FocusEvent) => {
        setFocused(true);
        onFocus?.(event);
    };

    const should_report_errors = is_editable && name && name !== NoName && label;

    // biome-ignore lint/correctness/useExhaustiveDependencies(errorBag): API
    useEffect(() => {
        if (should_report_errors && touched) {
            const { has, add, forget } = errorBag;
            const value = textInputProps.value;
            const textContentType = textInputProps.textContentType;
            const dirty = (value ?? "") !== last;

            if (has(name) && (dirty || optional)) {
                forget(name);
            }

            if (!focused) {
                if (!optional && empty(value)) {
                    add(name, `${label} cannot be blank.`);
                } else if (textContentType === "emailAddress" && filled(value) && !isEmail(value)) {
                    add(name, `${label} is not valid.`);
                }
            }

            dirty && setLast(value ?? "");
        }
    }, [
        should_report_errors,
        optional,
        focused,
        touched,
        textInputProps.value,
        textInputProps.textContentType,
        label,
        name,
        last,
    ]);

    const handleOnBlur = (event: BlurEvent) => {
        setFocused(false);
        setTouched(true);
        onBlur?.(event);
    };

    const handleOnChangeText = (text: string) => {
        onChangeText?.(text);
        setTouched(true);
    };

    return (
        <FieldLabel
            labelStyle={labelStyle}
            label={label}
            hideLabel={hideLabel || !show_label}
            optional={optional}
            error={field_error}
            style={containerStyle}
            hint={hint}
        >
            <View style={styles.container}>
                <TextInputBase
                    {...textInputProps}
                    editable={is_editable}
                    placeholderTextColor={inputs.placeholder.color}
                    onFocus={handleOnFocus}
                    onBlur={handleOnBlur}
                    onChangeText={handleOnChangeText}
                    style={[
                        inputs.input,
                        inputs.inputText,
                        Boolean(field_error) && inputs.error,
                        focused && inputs.focus,
                        disabled && [inputs.disabled, inputs.disabledText],
                        { paddingLeft, paddingRight },
                        style,
                    ]}
                    ref={field_ref}
                />
                {filled(include_left) && (
                    <View style={styles.left} onLayout={onLeftLayout}>
                        {include_left()}
                    </View>
                )}
                {filled(include_right) && (
                    <View style={styles.right} onLayout={onRightLayout}>
                        {include_right()}
                    </View>
                )}
            </View>
        </FieldLabel>
    );
};

const styles = StyleSheet.create({
    container: {
        position: "relative",
        alignItems: "stretch",
    },
    left: {
        position: "absolute",
        top: 2,
        bottom: 2,
        left: Space.sm,
        justifyContent: "center",
        alignItems: "flex-start",
        zIndex: 10,
    },
    right: {
        position: "absolute",
        top: 2,
        bottom: 2,
        right: Space.sm,
        justifyContent: "center",
        alignItems: "flex-end",
        zIndex: 10,
    },
});
